/**
 * Session timeout alert utility
 */

let currentAlert: HTMLDivElement | null = null
let hideTimer: NodeJS.Timeout | null = null

const removeAlert = (): void => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }

  if (currentAlert && currentAlert.parentNode) {
    currentAlert.style.animation = 'slideUp 0.3s ease-out'
    setTimeout(() => {
      if (currentAlert && currentAlert.parentNode) {
        currentAlert.parentNode.removeChild(currentAlert)
      }
      currentAlert = null
    }, 300)
  }
}

export const showSessionTimeoutAlert = (): (() => void) => {
  removeAlert()

  const alert = document.createElement('div')
  alert.id = 'session-timeout-alert'
  alert.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #00338e;
    color: white;
    padding: 12px 24px;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: slideDown 0.3s ease-out;
  `

  // Add spinner
  const spinner = document.createElement('span')
  spinner.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 16 16" style="animation: spin 1s linear infinite;">
      <circle cx="8" cy="8" r="6" fill="none" stroke="white" stroke-width="2" stroke-dasharray="31.4" stroke-dashoffset="10" />
    </svg>
  `
  alert.appendChild(spinner)

  // Add text
  const text = document.createElement('span')
  text.textContent = 'Session timed out. Refreshing...'
  alert.appendChild(text)

  // Add animations
  const style = document.createElement('style')
  style.textContent = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(style)

  document.body.appendChild(alert)
  currentAlert = alert

  const autoRemoveTimer = setTimeout(() => {
    removeAlert()
  }, 10000)

  return () => {
    clearTimeout(autoRemoveTimer)
    removeAlert()
  }
}

export const showSuccessAlert = (message: string, duration = 3000): void => {
  removeAlert()

  const alert = document.createElement('div')
  alert.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #00338e;
    color: white;
    padding: 12px 24px;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: slideDown 0.3s ease-out;
  `

  // Add checkmark icon
  const icon = document.createElement('span')
  icon.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
      <path d="M13.5 3.5L6 11L2.5 7.5L3.5 6.5L6 9L12.5 2.5L13.5 3.5Z" />
    </svg>
  `
  alert.appendChild(icon)

  // Add text
  const text = document.createElement('span')
  text.textContent = message
  alert.appendChild(text)

  document.body.appendChild(alert)
  currentAlert = alert

  hideTimer = setTimeout(() => removeAlert(), duration)
}

export const showErrorAlert = (message: string, duration = 4000): void => {
  removeAlert()

  const alert = document.createElement('div')
  alert.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #d32f2f;
    color: white;
    padding: 12px 24px;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: slideDown 0.3s ease-out;
  `

  // Add X icon
  const icon = document.createElement('span')
  icon.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
      <path d="M4 4L12 12M12 4L4 12" stroke="white" stroke-width="2" stroke-linecap="round" />
    </svg>
  `
  alert.appendChild(icon)

  // Add text
  const text = document.createElement('span')
  text.textContent = message
  alert.appendChild(text)

  document.body.appendChild(alert)
  currentAlert = alert

  hideTimer = setTimeout(() => removeAlert(), duration)
}
